import { useState, useMemo } from 'react';
import { NetworkSelect } from '../../../components/networkSelect';
import { useStakingStore } from '@/store/staking';
import { scTrack } from '@web3/base-tracking';
import { Event, Page } from '@/config/track-config';
import { POOL_TAB } from '@/utils/constants';
import { useFm } from '@web3/base-hooks';
import { Tabs } from '@bfe/moly';
import cls from 'classnames';
import { useParams } from '@web3/base-hooks';

import { RowBox } from './rowBox';
import { ColBox } from './colBox';

import commonStyles from '@/pages/[locale]/index.module.scss';
import styles from './index.module.less';
import { appPath } from '@web3/base-utils';

const METH_STAKING_LINK = 'https://meth.mantle.xyz/stake';

const DIRECTION = {
  ROW: 'row',
  COL: 'col',
};

export const Pool = () => {
  const fm = useFm();
  const { thirdChains, poolInfo, originPoolList } = useStakingStore();
  const [direction, setDirection] = useState(DIRECTION.ROW);
  const [network, setNetwork] = useState('');
  const [currentTab, setCurrentTab] = useState(POOL_TAB.SINGLE);
  const { locale } = useParams();
  const tabList = useMemo(() => {
    return [
      //单币
      {
        key: POOL_TAB.SINGLE,
        label: <h3>{fm('singleCoin')}</h3>,
        children: <div className={commonStyles.tabBox} />,
      },
      // 稳定币
      {
        key: POOL_TAB.STABLE,
        label: <h3>{fm('stableCoin')}</h3>,
        children: <div className={commonStyles.tabBox} />,
      },
      // 积分
      {
        key: POOL_TAB.POINTS,
        label: <h3>{fm('points')}</h3>,
        children: <div className={commonStyles.tabBox} />,
      },
      // 再质押
      {
        key: POOL_TAB.RESTAKE,
        label: <h3>{fm('restake')}</h3>,
        children: <div className={commonStyles.tabBox} />,
      },
      // 奖励金
      {
        key: POOL_TAB.REWARD,
        label: <h3>{fm('rewardMoney')}</h3>,
        children: <div className={commonStyles.tabBox} />,
      },
      // 多币
      {
        key: POOL_TAB.MULTI,
        label: <h3>{fm('multiCurrency')}</h3>,
        children: <div className={commonStyles.tabBox} />,
      },
    ].filter((item) => {
      return (
        Object.keys(poolInfo).includes(item.key) && poolInfo[item.key]?.length
      );
    });
  }, [fm, poolInfo]);

  const handleTabChange = (val: string) => {
    scTrack(Event.Web3UserClick, {
      page_name: Page.StakingWebHomepage,
      tab_poolname: val,
    });
    setCurrentTab(val);
  };

  const handleStakingClick = ({
    id,
    poolCode,
  }: {
    id: number;
    poolCode: string;
  }) => {
    if (!id) return;

    // 埋点
    scTrack(Event.Web3UserClick, {
      page_name: Page.StakingWebHomepage,
      spmID: id,
    });

    if (poolCode === 'METH') {
      window.top?.open(METH_STAKING_LINK);
      return;
    }
    window.top?.open(`/${locale}${appPath}/pool/${id}`, '_self');
  };

  const poolList = useMemo(() => {
    return poolInfo[currentTab] || [];
  }, [currentTab, poolInfo]);

  const isCol = useMemo(() => {
    return direction === DIRECTION.COL;
  }, [direction]);

  const networkList = useMemo(() => {
    return [
      {
        chainCode: '',
        chainName: fm('allNetwork'),
        iconNightUrl: '',
      },
      ...thirdChains.filter(
        (v) =>
          originPoolList.findIndex(
            (n: { chainCode: any }) => n.chainCode === v.chainCode
          ) !== -1
      ),
    ].map((item: any) => {
      const { chainCode, chainName } = item;
      item.value = chainCode;
      item.label = chainName;
      return item;
    });
  }, [thirdChains, fm, originPoolList]);

  const handleNetworkChange = (val: string) => {
    setNetwork(val);

    scTrack(Event.Web3UserClick, {
      page_name: Page.StakingWebHomepage,
      button_name: 'Sort_selectorswitch',
      sort_name: val,
    });
  };

  return (
    <div className={cls(styles.container)}>
      <h2 className={cls(styles.title)}>{fm('oneClickStake')}</h2>
      <div className={styles.tabsArea}>
        <Tabs
          size="xl"
          items={tabList}
          activeKey={currentTab}
          onChange={handleTabChange}
          className={commonStyles.tabContainer}
          tabTitleClassName={commonStyles.tabTitleContainer}
        />

        {/* 切换布局组件 和 切换网络组件 */}
        <div className={cls('flex items-center', styles.tabRt)}>
          <span
            className={cls(
              'transition-all cursor-pointer',
              styles.directionIcon,
              styles.directionRowIcon,
              {
                [styles.directionBrandIcon]: !isCol,
              }
            )}
            data-spm-even={Event.Web3UserClick}
            data-spm-data={JSON.stringify({
              page_name: Page.StakingWebHomepage,
              button_name: 'Sort_selectorswitch',
              tab_poolname: DIRECTION.ROW,
            })}
            onClick={() => setDirection(DIRECTION.ROW)}
          />
          <span
            className={cls(
              'transition-all cursor-pointer',
              styles.directionIcon,
              styles.directionColIcon,
              {
                [styles.directionBrandIcon]: isCol,
              }
            )}
            data-spm-even={Event.Web3UserClick}
            data-spm-data={JSON.stringify({
              page_name: Page.StakingWebHomepage,
              button_name: 'Sort_selectorswitch',
              tab_poolname: DIRECTION.COL,
            })}
            onClick={() => setDirection(DIRECTION.COL)}
          />
          <div className={styles.line}></div>
          <div
            data-spm-even={Event.Web3UserClick}
            data-spm-data={JSON.stringify({
              page_name: Page.StakingWebHomepage,
              button_name: 'Sort_selectorswitch',
              tab_poolname: 'network',
            })}
          >
            <NetworkSelect
              network={network}
              options={networkList}
              onChange={handleNetworkChange}
            />
          </div>
        </div>
      </div>

      {!isCol && (
        <RowBox
          poolList={poolList}
          currentTab={currentTab}
          network={network}
          networkList={networkList}
          handleStakingClick={handleStakingClick}
        />
      )}

      {isCol && (
        <ColBox
          poolList={poolList}
          currentTab={currentTab}
          network={network}
          networkList={networkList}
          handleStakingClick={handleStakingClick}
        />
      )}
    </div>
  );
};
